// Note (Evan): This component can be safely removed once `inquiry:use-component-styles` is fully on
// and the default behavior of components deriving styles from style-variants on the server
import { useCallback, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { change } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { UploadedAsset } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import {
  SelectStyleInput,
  StyleInput,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { RootState } from 'dashboard/redux/store';
import { StyleValueSizeConfig } from 'lib/inquiryStyles';
import {
  BaseComponentConfig,
  ImageHostedComponentConfig,
  isImageHostedComponentConfig,
  SizingKeys,
  SizingUnitTypes,
  UiComponentConfig,
} from 'lib/interfaces/ui-components';

// Note (Evan): this is static for now, but we will need to derive this value from the screen size and screen padding
const STATIC_FULL_WIDTH_PX = 400 - 2 * 24;

type DimensionEditOptions = {
  hidden?: boolean;
  disabled?: boolean;
  hideInstrinsicOption?: boolean;
};

interface ConfigSizingComponent extends BaseComponentConfig {
  name: string;
  config: Record<string, unknown> & Partial<{ height?: string; width?: string }>;
}

type SizingStylesProps = {
  component: ConfigSizingComponent;
  componentBasePath: string;
  styleVariantIndex: number;
  editable: boolean;
  submitStyle: (field: string, values: any) => void;
  heightOptions: DimensionEditOptions;
  widthOptions: DimensionEditOptions;
  fallbackDimensions?: {
    width: StyleValueSizeConfig;
    height: StyleValueSizeConfig;
  };
};

type SizingStyleAttributes = {
  width: {
    label: string;
    default: StyleValueSizeConfig;
  };
  height: {
    label: string;
    default: StyleValueSizeConfig;
  };
};

const DEFAULT_SIZING_ATTRIBUTES: SizingStyleAttributes = {
  width: {
    label: 'Width',
    default: {
      value: 0,
      unit: 'px',
    },
  },
  height: {
    label: 'Height',
    default: {
      value: 0,
      unit: 'px',
    },
  },
};

// This component handles styling specifically for components
// that derive height and width from the `config` attribute on component config
const ConfigSizingStyles = ({
  component,
  componentBasePath,
  fallbackDimensions,
  editable,
  heightOptions,
  widthOptions,
  styleVariantIndex,
  submitStyle,
}: SizingStylesProps) => {
  const dispatch = useDispatch();
  const assets = useSelector<RootState, UploadedAsset[] | undefined>(
    (state) => state.inquiryTemplate.assets,
  );

  const sizeDefaults: SizingStyleAttributes = useMemo(() => {
    const sizingMapTuples = Object.entries(DEFAULT_SIZING_ATTRIBUTES).map((styleAssignment) => {
      const [style, props] = styleAssignment;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const defaultValue = component.config?.[style as SizingKeys];
      if (defaultValue != null) {
        return [
          style,
          {
            ...props,
            default: {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              unit: defaultValue.split(/\d+/)[1],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value: parseInt(defaultValue),
            },
          },
        ];
      } else {
        return [
          style,
          {
            ...props,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            default: fallbackDimensions?.[style as SizingKeys] ?? props.default,
          },
        ];
      }
    });

    return Object.fromEntries(sizingMapTuples);
  }, [component.config, fallbackDimensions]);

  const configPath = `steps.${componentBasePath}.config`;

  const submitField = useCallback(
    (value: string, field: string, unit?: SizingUnitTypes) => {
      const savedValue = parseInt(value) >= 0 ? parseInt(value) : 0;

      // we need to update two places:
      // 1) property on component config
      // 2) property on the style variant.
      // This is to keep values in both places in sync before completely relying on component style variants
      // when `inquiry:use-component-styles` is on.
      // `inquiry:use-component-styles` replaces the source of how property styles are derived on the server
      // When it's on, it will pull styles from the style-variant rather than config
      batch(() => {
        submitStyle(field, {
          value: savedValue,
          unit: unit ?? 'px',
        });

        dispatch(
          change(
            INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
            `${configPath}.${field}`,
            `${savedValue}${unit ?? 'px'}`,
          ),
        );
      });
    },
    [configPath, dispatch, submitStyle],
  );

  const selectSizeUnit = useCallback(
    (value: SizingUnitTypes, field: string) => {
      let newSizeValue = '100';
      if (value === 'px' && isImageHostedComponentConfig(component as UiComponentConfig)) {
        const assetId = (component as ImageHostedComponentConfig).config['asset-id'];
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const aspectRatio = assets.find((asset) => asset['blob-id'] === assetId)?.['aspect-ratio'];
        const defaultPixelValue =
          field === 'height' && aspectRatio
            ? STATIC_FULL_WIDTH_PX / aspectRatio
            : STATIC_FULL_WIDTH_PX;
        newSizeValue = defaultPixelValue.toString();
      }
      submitField(newSizeValue, field, value);
    },
    [submitField, component.config, assets],
  );

  const editableWidth = editable && !widthOptions?.disabled;
  const editableHeight = editable && !heightOptions?.disabled;

  return (
    <SectionAccordion title='Sizing'>
      {!heightOptions?.hidden && (
        <SectionRow>
          <SectionColumn>
            <StyleInput
              label={sizeDefaults.height.label}
              field='height'
              name={`component-style-height-${component.name}-${styleVariantIndex}`}
              defaultValue={sizeDefaults.height.default.value.toString()}
              editable={editableHeight && sizeDefaults.height.default.unit !== '%'}
              submit={submitField}
              {...(!heightOptions?.hideInstrinsicOption && {
                unitValue: sizeDefaults.height.default.unit,
              })}
            />
          </SectionColumn>
          {!heightOptions?.hideInstrinsicOption && (
            <SectionColumn>
              <SelectStyleInput
                name='resizing-height'
                editable={editableHeight}
                defaultValue={sizeDefaults.height.default.unit}
                label='Resizing'
                placeholder='Select height style'
                field='height'
                options={[
                  { label: 'Fixed height', value: 'px' },
                  { label: 'Fill height', value: '%' },
                ]}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onChange={selectSizeUnit}
              />
            </SectionColumn>
          )}
        </SectionRow>
      )}

      {!widthOptions?.hidden && (
        <SectionRow>
          <SectionColumn>
            <StyleInput
              label={sizeDefaults.width.label}
              field='width'
              name={`component-style-width-${component.name}-${styleVariantIndex}`}
              defaultValue={sizeDefaults.width.default.value.toString()}
              editable={editableWidth && sizeDefaults.width.default.unit !== '%'}
              submit={submitField}
              {...(!widthOptions?.hideInstrinsicOption && {
                unitValue: sizeDefaults.width.default.unit,
              })}
            />
          </SectionColumn>
          {!widthOptions?.hideInstrinsicOption && (
            <SectionColumn>
              <SelectStyleInput
                name='resizing-width'
                editable={editableWidth}
                defaultValue={sizeDefaults.width.default.unit}
                label='Resizing'
                placeholder='Select width style'
                field='width'
                options={[
                  { label: 'Fixed width', value: 'px' },
                  { label: 'Fill width', value: '%' },
                ]}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onChange={selectSizeUnit}
              />
            </SectionColumn>
          )}
        </SectionRow>
      )}
    </SectionAccordion>
  );
};

export default ConfigSizingStyles;
